import type { QueryClient } from "@tanstack/react-query"
import { HeadContent, Outlet, Scripts, createRootRouteWithContext } from "@tanstack/react-router"
import { createServerFn } from "@tanstack/react-start"
// Static import - TanStack Start handles tree-shaking for server functions
import { getRequest } from "@tanstack/react-start/server"
import { lazy, Suspense } from "react"
import type { BrandConfig } from "@/lib/brand-config"
import { useFeatureFlagSync } from "@/features/feature-flags/hooks/useFeatureFlagSync"
import { evaluateFlagFn } from "@/features/feature-flags/server/flagEvaluation"
import { getLanguageFromRequest } from "@/i18n/i18n"
import { loadCatalog } from "@/i18n/loadMessages"
import { auth } from "@/lib/auth"
import { useSession } from "@/lib/auth-client"
import { getBrandConfig } from "@/lib/brand-config.server"
import { BrandConfigProvider } from "@/lib/brand-context"
import { generateBrandCss } from "@/lib/brand-css"
import appCss from "@/styles.css?url"
import { Providers } from "../providers"

const getAuthData = createServerFn({ method: "GET" }).handler(async () => {
  try {
    const request = getRequest()
    // Guard against getRequest() returning null/undefined during SSR
    if (!request?.headers) {
      return { user: null, token: null }
    }
    const session = await auth.api.getSession({ headers: request.headers })
    return { user: session?.user || null, token: session?.session.token || null }
  } catch {
    // If getRequest() throws during SSR/hydration, return unauthenticated state
    return { user: null, token: null }
  }
})

const getBrandConfigFn = createServerFn({ method: "GET" }).handler(
  async (): Promise<BrandConfig> => getBrandConfig(),
)

type MyRouterContext = {
  language: string
  authData: Awaited<ReturnType<typeof getAuthData>>
  queryClient: QueryClient
}

const BRAND_CONFIG_QUERY_KEY = ["brandConfig"] as const

export const Route = createRootRouteWithContext<MyRouterContext>()({
  beforeLoad: async ({ context }) => {
    // Ensure auth data is fetched and properly cached before proceeding
    const authData = await context.queryClient.ensureQueryData({
      queryKey: ["authData"],
      queryFn: ({ signal }) => getAuthData({ signal }),
      staleTime: 1000 * 60 * 5, // Keep auth data fresh for 5 minutes
    })

    let internationalizationEnabled = false
    try {
      const flag = await evaluateFlagFn({
        data: { flagKey: "internationalization", defaultValue: false },
      })
      internationalizationEnabled = flag.value === true
    } catch {
      internationalizationEnabled = false
    }

    let language = "en"
    try {
      if (internationalizationEnabled) {
        language = await getLanguageFromRequest()
      }
    } catch {
      language = "en" // Fallback to English if server function fails
    }
    await loadCatalog(language)

    // Brand config is computed server-side from runtime process.env.
    // Cache on the queryClient so SSR-Query dehydrates the server value into the
    // client cache; subsequent client-side beforeLoad runs read from cache and
    // never depend on import.meta.env values (which are baked in at build time).
    const brandConfig = await context.queryClient.ensureQueryData({
      queryKey: BRAND_CONFIG_QUERY_KEY,
      queryFn: ({ signal }) => getBrandConfigFn({ signal }),
      staleTime: Infinity,
      gcTime: Infinity,
    })

    return {
      language,
      token: authData.token,
      user: authData.user,
      authData,
      brandConfig,
    }
  },
  loader: ({ context }) => {
    return {
      language: context.language,
      brandConfig: context.brandConfig,
    }
  },
  head: ({ loaderData }) => ({
    meta: [
      {
        charSet: "utf-8",
      },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1",
      },
      {
        name: "theme-color",
        media: "(prefers-color-scheme: light)",
        content: "oklch(1 0 0)",
      },
      {
        name: "theme-color",
        media: "(prefers-color-scheme: dark)",
        content: "oklch(1 0 0)",
      },
      {
        title: loaderData?.brandConfig.name ?? "Shelfi",
      },
    ],
    links: [
      {
        rel: "preconnect",
        href: "https://fonts.googleapis.com",
      },
      {
        rel: "preconnect",
        href: "https://fonts.gstatic.com",
        crossOrigin: "anonymous",
      },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Ubuntu:ital,wght@0,300;0,400;0,500;0,700;1,400;1,700&display=swap",
      },
      {
        rel: "stylesheet",
        href: appCss,
      },
      { rel: "icon", href: loaderData?.brandConfig.faviconUrl ?? "/favicon.ico" },
    ],
  }),

  component: () => {
    return <RootLayoutComponent />
  },
})

const DevtoolsClient = lazy(() => import("../components/DevtoolsClient"))
const SentryToolbarClient = lazy(() => import("../components/SentryToolbarClient"))

function RootLayoutComponent() {
  const { data: session } = useSession()
  const isAuthenticated = !!session?.user
  const isRb2Email = session?.user?.email?.endsWith("@rb2.nl") ?? false

  // Enable real-time feature flag sync for authenticated users
  useFeatureFlagSync({ enabled: isAuthenticated })

  return (
    <RootDocument>
      <Outlet />
      {import.meta.env.DEV && (
        <Suspense fallback={null}>
          <DevtoolsClient />
        </Suspense>
      )}
      {import.meta.env.DEV && isRb2Email && (
        <Suspense fallback={null}>
          <SentryToolbarClient enabled={true} />
        </Suspense>
      )}
    </RootDocument>
  )
}

function RootDocument({ children }: { children: React.ReactNode }) {
  const { language, brandConfig } = Route.useRouteContext()
  const brandCss = generateBrandCss(brandConfig)

  return (
    <html lang={language} suppressHydrationWarning>
      <head>
        <HeadContent />
        {brandCss && <style id="brand-overrides" dangerouslySetInnerHTML={{ __html: brandCss }} />}
      </head>
      <body className={"overflow-hidden"}>
        <BrandConfigProvider config={brandConfig}>
          <Providers>{children}</Providers>
        </BrandConfigProvider>
        <Scripts />
      </body>
    </html>
  )
}
